import React, { useMemo, useState } from "react";

import {
  ArrowRight,
  Building2,
  ChevronRight,
  Circle,
  CircleCheck,
  Clock,
  Hourglass,
  Info,
  Layers,
  ListFilter,
  Search,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";

import {
  CadenceFilterValue,
  classifyYourStatus,
  matchesAccountCadenceFilter,
  matchesDigestStaleOrPending,
  matchesMyCadenceFilter,
  parseKpiFilterFromUrl,
  parseMyCadenceFromUrl,
} from "./assessmentStatusFilter";
import AssessmentStatusOverviewCards from "./AssessmentStatusOverviewCards";
import {
  getHeldDisconnectInputHint,
  getPrivateRemainderPendingHint,
} from "./assessmentSubmitSuccessCopy";
import DropdownSwitcher from "../../Components/DropdownSwitcher";
import HoverHint from "../../Components/HoverHint";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../intelligence-briefing-library/components/ui/tooltip";
import type {
  PortfolioAccount,
  PortfolioRosterPerson,
} from "../../Store/Reducers/CustomerWorkspaceSlice";
import {
  formatAssessmentDateUtc,
  parseAssessmentDate,
  serverNow,
} from "../../Utils/Helper/assessmentDateUtils";
import { toCompactLevelLabel } from "../../Utils/Helper/levelUtils";

type PortfolioAccountsStatusPanelProps = {
  accounts: PortfolioAccount[];
  orgAccounts?: PortfolioAccount[];
  error: string | null;
  onAssess?: (account: PortfolioAccount) => void;
  onViewFullReport?: (account: PortfolioAccount) => void;
  /** ESL, vendor admin, and Exec Ops may open Report while a cycle is still running. */
  allowInProgressReport?: boolean;
  mode?: "my" | "org";
  showViewTabs?: boolean;
};

const headerInfoIconClass =
  "inline-flex h-3.5 w-3.5 shrink-0 cursor-pointer items-center justify-center rounded-full border border-gray-200 bg-gray-50 text-gray-400 transition-colors hover:border-purple-300 hover:bg-purple-50 hover:text-purple-600 focus:outline-none focus-visible:border-purple-400 focus-visible:text-purple-600 data-[state=open]:border-purple-300 data-[state=open]:bg-purple-50 data-[state=open]:text-purple-600";

const headerTooltipClass =
  "z-[9999] max-w-[320px] border border-gray-200 bg-white px-3 py-2.5 text-[12.5px] font-normal normal-case tracking-normal leading-relaxed text-gray-800 shadow-[0_4px_16px_rgba(0,0,0,0.12)]";

type StatusHeader = { label: string; tooltip?: string };

const TableHeaderWithInfo: React.FC<{
  label: string;
  tooltip?: string;
}> = ({ label, tooltip }) => {
  if (!tooltip) return <>{label}</>;
  return (
    <Tooltip>
      <div className="inline-flex items-center gap-1 whitespace-nowrap">
        <span>{label}</span>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={headerInfoIconClass}
            aria-label={`About ${label}`}
          >
            <Info className="h-2 w-2" strokeWidth={2.5} aria-hidden />
          </button>
        </TooltipTrigger>
      </div>
      <TooltipContent
        side="bottom"
        align="start"
        sideOffset={8}
        className={headerTooltipClass}
      >
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
};

const MY_CADENCE_FILTER_OPTIONS = [
  { label: "All", value: "all", icon: Layers },
  { label: "Current", value: "current", icon: CircleCheck },
  { label: "Stale", value: "stale", icon: Clock },
  { label: "Unassessed", value: "not_assessed", icon: Circle },
  { label: "Pending", value: "pending", icon: Hourglass },
];

const ORG_STATUS_FILTER_OPTIONS = [
  { label: "All", value: "all", icon: Layers },
  { label: "Pending", value: "pending", icon: Hourglass },
  { label: "Completed", value: "completed", icon: CircleCheck },
  { label: "No assessment", value: "none", icon: Circle },
];

const rosterKindName = (kind?: string | null): string | null => {
  const value = (kind || "").toLowerCase();
  if (value === "formal") return "Formal";
  if (value === "team" || value === "self" || value === "self_assessment") {
    return "Team";
  }
  if (value === "disconnect") return "Disconnect";
  return null;
};

const titleDateLabel = (value?: string | null): string | null => {
  const date = parseDisplayDate(value);
  if (!date) return null;
  return `${MONTH_SHORT[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
};

const startedPhrase = (ageDays?: number | null): string | null => {
  if (ageDays == null) return null;
  if (ageDays <= 0) return "Started today";
  if (ageDays === 1) return "Started 1 day ago";
  return `Started ${ageDays} days ago`;
};

const expandRowTitle = (account: PortfolioAccount): string | null => {
  const kind = rosterKindName(
    account.response_roster_kind || account.org_status_kind
  );
  if (account.response_roster_source === "running") {
    const name = kind ? `Pending ${kind} assessment` : "Pending assessment";
    const started = startedPhrase(account.org_status_age_days);
    return started ? `${name} · ${started}` : name;
  }
  if (account.response_roster_source === "published") {
    const name = kind ? `Published ${kind} assessment` : "Published assessment";
    const level = getLevelLabel(account.published_level);
    if (!level) return name;
    const date = titleDateLabel(account.published_at);
    return date ? `${name} · ${level} · ${date}` : `${name} · ${level}`;
  }
  return null;
};

const kindLabel = (kind?: string | null): string => {
  if (kind === "team") return "Team";
  if (kind === "formal") return "Formal";
  if (kind === "disconnect") return "Disconnect";
  return "";
};

const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

const getLevelLabel = (level?: string | null): string | null => {
  if (!level) return null;
  const trimmed = level.trim();
  if (!trimmed) return null;
  return /\d/.test(trimmed) ? toCompactLevelLabel(trimmed) : trimmed;
};

const formatLongDate = (value?: string | null): string | null => {
  const date = parseDisplayDate(value);
  if (!date) {
    return value ? formatAssessmentDateUtc(value, "US_LONG") || value : null;
  }
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${MONTH_SHORT[date.getUTCMonth()]} ${day}, ${date.getUTCFullYear()}`;
};

const parseDisplayDate = (value?: string | null): Date | null => {
  const parsed = parseAssessmentDate(value);
  if (parsed) return parsed;
  if (!value) return null;
  const match = value.trim().match(/^([A-Za-z]{3})\s+(\d{1,2}),\s+(\d{4})$/);
  if (!match) return null;
  const month = MONTH_SHORT.indexOf(match[1] as (typeof MONTH_SHORT)[number]);
  if (month < 0) return null;
  return new Date(Date.UTC(Number(match[3]), month, Number(match[2])));
};

const daysAgoLabel = (value?: string | null): string | null => {
  const date = parseDisplayDate(value);
  if (!date) return null;
  const today = serverNow();
  const days = Math.round(
    (Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()) -
      Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())) /
      86400000
  );
  return `${Math.max(0, days)}D ago`;
};

const slashDateLabel = (value?: string | null): string | null => {
  const date = parseDisplayDate(value);
  if (!date) return null;
  return `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${date.getUTCFullYear()}`;
};

const slashDateFromAge = (ageDays?: number | null): string | null => {
  if (ageDays == null) return null;
  const today = serverNow();
  const date = new Date(
    Date.UTC(
      today.getUTCFullYear(),
      today.getUTCMonth(),
      today.getUTCDate() - ageDays
    )
  );
  return `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${date.getUTCFullYear()}`;
};

const DateAgoHint: React.FC<{ date?: string | null; label: string }> = ({
  date,
  label,
}) => {
  const ago = daysAgoLabel(date);
  const text = <span className="w-fit text-xs text-gray-500">{label}</span>;
  if (!ago) return text;
  return (
    <HoverHint description={ago}>
      <span tabIndex={0} className="w-fit cursor-default text-xs text-gray-500">
        {label}
      </span>
    </HoverHint>
  );
};

const PrivateRemainderNote: React.FC<{
  pendingCountsOn?: string | null;
  held?: boolean;
}> = ({ pendingCountsOn, held }) => {
  const countsOnLabel = formatLongDate(pendingCountsOn);
  if (held) {
    return (
      <HoverHint
        title="Held during disconnect"
        description={getHeldDisconnectInputHint()}
      >
        <span
          tabIndex={0}
          className="inline-flex w-fit cursor-pointer items-center gap-1 text-xs font-medium text-amber-700"
        >
          Held · counts after the disconnect
          <Info className="h-3 w-3 shrink-0" aria-hidden />
        </span>
      </HoverHint>
    );
  }
  if (!countsOnLabel) return null;
  return (
    <HoverHint description={getPrivateRemainderPendingHint(countsOnLabel)}>
      <span
        tabIndex={0}
        className="inline-flex w-fit cursor-pointer items-center gap-1 text-xs font-medium text-amber-700"
      >
        Pending · counts on next assessment
        <Info className="h-3 w-3 shrink-0" aria-hidden />
      </span>
    </HoverHint>
  );
};

const CombinedAssessmentCell: React.FC<{
  date?: string | null;
  level?: string | null;
  pendingCountsOn?: string | null;
  held?: boolean;
}> = ({ date, level, pendingCountsOn, held }) => {
  const levelLabel = getLevelLabel(level);
  const dateLabel = formatLongDate(date);
  if (!dateLabel && !levelLabel) {
    return <span className="text-sm text-gray-400">—</span>;
  }
  return (
    <div className="flex flex-col">
      {levelLabel ? (
        <span className="text-sm font-semibold text-gray-900">
          {levelLabel}
        </span>
      ) : null}
      {dateLabel ? <DateAgoHint date={date} label={dateLabel} /> : null}
      <PrivateRemainderNote pendingCountsOn={pendingCountsOn} held={held} />
    </div>
  );
};

const myUpdateTextClass = (label: string): string => {
  const normalized = label.toLowerCase();
  if (normalized.includes("current")) return "text-emerald-600";
  if (normalized.includes("stale")) return "text-amber-700";
  return "text-gray-500";
};

const CadencePill: React.FC<{
  status?: string | null;
  date?: string | null;
  pendingCountsOn?: string | null;
  held?: boolean;
}> = ({ status, date, pendingCountsOn, held }) => {
  const label = (status ?? "").trim() || "Unassessed";
  const dateLabel = formatLongDate(date);
  return (
    <div className="flex flex-col">
      <span className={`text-sm font-medium ${myUpdateTextClass(label)}`}>
        {label}
      </span>
      {dateLabel ? <DateAgoHint date={date} label={dateLabel} /> : null}
      <PrivateRemainderNote pendingCountsOn={pendingCountsOn} held={held} />
    </div>
  );
};

const PersonStatusBadge: React.FC<{ status?: string }> = ({ status }) => {
  const completed = (status ?? "").toLowerCase() === "completed";
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
        completed
          ? "bg-emerald-50 text-emerald-700"
          : "bg-amber-50 text-amber-700"
      }`}
    >
      {completed ? "Completed" : "Pending"}
    </span>
  );
};

const RosterPersonRow: React.FC<{ person: PortfolioRosterPerson }> = ({
  person,
}) => (
  <div className="flex min-w-0 items-center gap-2">
    <span className="truncate text-sm font-semibold text-gray-900">
      {person.name}
    </span>
    <PersonStatusBadge status={person.status} />
  </div>
);

const ROSTER_COLUMNS: {
  role: "sponsor" | "account_team" | "other_executive";
  label: string;
}[] = [
  { role: "sponsor", label: "Assigned Sponsors" },
  { role: "account_team", label: "Account Team" },
  { role: "other_executive", label: "Other Executives" },
];

const RosterAccordion: React.FC<{ people: PortfolioRosterPerson[] }> = ({
  people,
}) => {
  const byRole = ROSTER_COLUMNS.map(column => ({
    ...column,
    people: people.filter(person => {
      const role = person.role || "other_executive";
      return role === column.role;
    }),
  }));

  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
      {byRole.map(column => (
        <div key={column.role} className="min-w-0">
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            {column.label}
          </p>
          {column.people.length > 0 ? (
            <div className="flex flex-col gap-2.5">
              {column.people.map(person => (
                <RosterPersonRow key={person.user_id} person={person} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400">—</p>
          )}
        </div>
      ))}
    </div>
  );
};

const StatusPhasePill: React.FC<{
  phase?: string | null;
  kind?: string | null;
  ageDays?: number | null;
  org?: boolean;
  fallbackDate?: string | null;
}> = ({ phase, kind, ageDays, org, fallbackDate }) => {
  if (!phase || phase === "none") {
    return org ? (
      <span className="inline-flex rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs font-medium text-gray-600">
        No assessment
      </span>
    ) : (
      <span className="text-sm text-gray-400">—</span>
    );
  }
  const label = `${phase === "pending" ? "Pending" : "Completed"}${
    ageDays != null ? ` ${ageDays}D` : ""
  }${kindLabel(kind) ? ` · ${kindLabel(kind)}` : ""}`;
  const tone =
    phase === "pending"
      ? "border-amber-200 bg-amber-50 text-amber-800"
      : "border-emerald-200 bg-emerald-50 text-emerald-800";
  const hoverDate = slashDateFromAge(ageDays) || slashDateLabel(fallbackDate);
  const pill = (
    <span
      tabIndex={hoverDate ? 0 : undefined}
      className={`inline-flex w-fit rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize ${tone}`}
    >
      {label}
    </span>
  );
  if (!hoverDate) return pill;
  return <HoverHint description={hoverDate}>{pill}</HoverHint>;
};

const PortfolioAccountsStatusPanel: React.FC<
  PortfolioAccountsStatusPanelProps
> = ({
  accounts,
  orgAccounts,
  error,
  onAssess,
  onViewFullReport,
  allowInProgressReport = false,
  mode: modeProp,
  showViewTabs = false,
}) => {
  const [viewMode, setViewMode] = useState<"my" | "org">(modeProp ?? "my");
  const mode = modeProp ?? viewMode;
  const rows = mode === "org" ? (orgAccounts ?? accounts) : accounts;
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [accountCadenceFilter, setAccountCadenceFilter] =
    useState<CadenceFilterValue>(() =>
      parseKpiFilterFromUrl(searchParams.get("kpi"))
    );
  const [myCadenceFilter, setMyCadenceFilter] = useState<string>(() =>
    parseMyCadenceFromUrl(searchParams.get("myCadence"))
  );
  // Digest CTA: show Stale ∪ Pending once; exit on any filter interaction.
  const [digestUnionActive, setDigestUnionActive] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const exitDigestDeepLink = () => {
    if (digestUnionActive) setDigestUnionActive(false);
    setSearchParams(
      prev => {
        if (!prev.has("myCadence") && !prev.has("kpi")) return prev;
        const next = new URLSearchParams(prev);
        next.delete("myCadence");
        next.delete("kpi");
        return next;
      },
      { replace: true }
    );
  };

  const handleKpiFilterClick = (filter: CadenceFilterValue) => {
    exitDigestDeepLink();
    setAccountCadenceFilter(filter);
  };

  const handleMyCadenceSelect = (value: string) => {
    exitDigestDeepLink();
    setMyCadenceFilter(value);
  };

  /** Scope for KPI counts. Digest deep link uses Stale ∪ Pending. */
  const scopedAccounts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return rows.filter(account => {
      if (mode === "org" && myCadenceFilter !== "all") {
        const phase = account.org_status_phase || "none";
        if (phase !== myCadenceFilter) return false;
      }
      if (mode !== "org") {
        if (digestUnionActive) {
          if (!matchesDigestStaleOrPending(account)) return false;
        } else if (
          !matchesMyCadenceFilter(
            account,
            myCadenceFilter as CadenceFilterValue
          )
        ) {
          return false;
        }
      }
      if (!q) return true;
      const haystack = [
        account.account_name,
        account.sector,
        account.current_level,
        account.assessment_status,
        account.org_assessment_status,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, mode, searchQuery, myCadenceFilter, digestUnionActive]);

  const filteredAccounts = useMemo(() => {
    // Digest landing lists the full union; do not AND KPI ∩ dropdown.
    if (digestUnionActive) return scopedAccounts;
    return scopedAccounts.filter(account =>
      matchesAccountCadenceFilter(account, accountCadenceFilter)
    );
  }, [scopedAccounts, accountCadenceFilter, digestUnionActive]);

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-1">
            {showViewTabs ? (
              <div className="inline-flex w-fit rounded-full border border-gray-200 bg-white px-1 py-1">
                <button
                  type="button"
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm ${mode === "my" ? "border border-purple-500 font-semibold text-purple-600" : "text-gray-500"}`}
                  onClick={() => {
                    exitDigestDeepLink();
                    setViewMode("my");
                    setMyCadenceFilter("all");
                  }}
                >
                  <ListFilter className="h-3.5 w-3.5" aria-hidden />
                  My Status
                </button>
                <button
                  type="button"
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm ${mode === "org" ? "border border-purple-500 font-semibold text-purple-600" : "text-gray-500"}`}
                  onClick={() => {
                    exitDigestDeepLink();
                    setViewMode("org");
                    setMyCadenceFilter("all");
                  }}
                >
                  <Building2 className="h-3.5 w-3.5" aria-hidden />
                  Org Status
                </button>
              </div>
            ) : (
              <h3 className="mb-0 text-xl font-semibold text-gray-900">
                Accounts
              </h3>
            )}
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <DropdownSwitcher
              className="shrink-0"
              value={myCadenceFilter}
              prefix={mode === "org" ? "Assessment Status: " : "My Update: "}
              options={
                mode === "org"
                  ? ORG_STATUS_FILTER_OPTIONS
                  : MY_CADENCE_FILTER_OPTIONS
              }
              onChange={handleMyCadenceSelect}
              listboxLabel={mode === "org" ? "Assessment Status" : "My Update"}
            />
            <div className="relative w-full sm:w-64">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                value={searchQuery}
                onChange={event => {
                  exitDigestDeepLink();
                  setSearchQuery(event.target.value);
                }}
                placeholder="Search accounts, sector, industry..."
                className="h-10 w-full rounded-md border border-gray-200 bg-gray-50/50 pl-10 pr-4 text-sm text-gray-900 placeholder:text-gray-400 focus:border-gray-300 focus:bg-white focus:outline-none focus:ring-1 focus:ring-gray-200"
              />
            </div>
          </div>
        </div>

        <AssessmentStatusOverviewCards
          accounts={scopedAccounts}
          activeFilter={accountCadenceFilter}
          onFilterClick={handleKpiFilterClick}
        />

        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <div className="overflow-auto rounded-xl border border-gray-200 bg-white">
          <table className="min-w-full border-collapse">
            <thead className="sticky top-0 z-10 bg-white">
              <tr className="border-b border-gray-100">
                {(mode === "org"
                  ? [
                      { label: "Account" },
                      {
                        label: "Published",
                        tooltip:
                          "Account-level published level plus account cadence. Examples: L3 - Current, L3 - Stale, or Unassessed. Hover the date for relative age (e.g. 12 D ago).",
                      },
                      {
                        label: "Assessment Status",
                        tooltip:
                          "Whether a Formal, Team, or Disconnect cycle is Pending or Completed for the account — with compact age, e.g. Pending 5D · Team or Completed 3D · Formal.",
                      },
                    ]
                  : [
                      { label: "Account" },
                      {
                        label: "Published",
                        tooltip:
                          "Account-level published level plus account cadence. Examples: L3 - Current, L3 - Stale, or Unassessed. Hover the date for relative age (e.g. 12 D ago).",
                      },
                      {
                        label: "My Assessment",
                        tooltip:
                          "Your latest submitted level and date for this account. None yet = you have never submitted; No response = the account is published but you did not respond.",
                      },
                      {
                        label: "My Update",
                        tooltip:
                          "Your personal assessment cadence for this account. Current shows your level (e.g. L3 - Current); Stale and Unassessed are label-only.",
                      },
                      {
                        label: "Assessment Status",
                        tooltip:
                          "Whether a Formal, Team, or Disconnect cycle is Pending or Completed for you — with compact age, e.g. Pending 5D · Team or Completed 3D · Formal.",
                      },
                      { label: "Action" },
                    ]
                ).map((header: StatusHeader) => (
                  <th
                    key={header.label}
                    className="px-4 py-3 text-left align-middle text-xs font-semibold uppercase tracking-wide text-gray-500"
                  >
                    <TableHeaderWithInfo
                      label={header.label}
                      tooltip={header.tooltip}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {filteredAccounts.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-12 text-center text-sm text-gray-500"
                  >
                    No accounts found
                  </td>
                </tr>
              ) : (
                filteredAccounts.map(account => {
                  const isExpanded = expandedId === account.account_id;
                  return (
                    <React.Fragment key={account.account_id}>
                      <tr className="transition-colors hover:bg-gray-50/60">
                        <td className="px-4 py-3">
                          <div className="flex min-w-0 items-start gap-2">
                            <button
                              type="button"
                              aria-label={
                                isExpanded
                                  ? "Collapse account team"
                                  : "Expand account team"
                              }
                              className="mt-0.5 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                              onClick={() =>
                                setExpandedId(
                                  isExpanded ? null : account.account_id
                                )
                              }
                            >
                              <ChevronRight
                                className={`h-4 w-4 transition-transform ${
                                  isExpanded ? "rotate-90" : ""
                                }`}
                              />
                            </button>
                            <div className="min-w-0">
                              <p
                                className="truncate text-sm font-semibold text-gray-900"
                                title={account.account_name}
                              >
                                {account.account_name}
                              </p>
                              {account.sector ? (
                                <p className="truncate text-xs text-gray-500">
                                  {account.sector}
                                </p>
                              ) : null}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {account.published_level ? (
                            <CombinedAssessmentCell
                              date={account.published_at}
                              level={account.published_level}
                            />
                          ) : (
                            <span className="text-sm italic text-gray-400">
                              Unassessed
                            </span>
                          )}
                        </td>
                        {mode === "my" ? (
                          <td className="px-4 py-3">
                            {account.your_published_level ? (
                              <CombinedAssessmentCell
                                date={account.your_published_date}
                                level={account.your_published_level}
                              />
                            ) : (
                              <span className="text-sm italic text-gray-400">
                                Unassessed
                              </span>
                            )}
                          </td>
                        ) : null}
                        {mode === "my" ? (
                          <td className="px-4 py-3">
                            <CadencePill
                              date={account.your_assessment_date}
                              pendingCountsOn={
                                account.your_assessment_pending_counts_on
                              }
                              held={account.your_assessment_held}
                              status={
                                account.your_assessment_level &&
                                classifyYourStatus(
                                  account.assessment_status
                                ) !== "not_assessed"
                                  ? `${getLevelLabel(account.your_assessment_level)} - ${classifyYourStatus(account.assessment_status) === "stale" ? "Stale" : "Current"}`
                                  : account.assessment_status
                              }
                            />
                          </td>
                        ) : null}
                        <td className="px-4 py-3">
                          <StatusPhasePill
                            phase={
                              mode === "org"
                                ? account.org_status_phase
                                : account.status_phase
                            }
                            kind={
                              mode === "org"
                                ? account.org_status_kind
                                : account.status_kind
                            }
                            ageDays={
                              mode === "org"
                                ? account.org_status_age_days
                                : account.status_age_days
                            }
                            fallbackDate={
                              account.published_at || account.onboarded_at
                            }
                            org={mode === "org"}
                          />
                        </td>
                        {mode === "my" && onAssess ? (
                          <td className="whitespace-nowrap px-4 py-3 align-middle">
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-900 hover:bg-gray-50"
                              onClick={() => onAssess(account)}
                            >
                              Assess
                              <ArrowRight className="h-3.5 w-3.5" />
                            </button>
                          </td>
                        ) : null}
                      </tr>
                      {isExpanded ? (
                        <tr>
                          <td
                            colSpan={mode === "my" ? 6 : 3}
                            className="bg-[#fafafa] px-12 py-5"
                          >
                            <div className="mb-4 flex items-center justify-between gap-3">
                              {expandRowTitle(account) ? (
                                <p className="mb-0 text-sm font-medium text-gray-900">
                                  {expandRowTitle(account)}
                                </p>
                              ) : (
                                <span />
                              )}
                              <button
                                type="button"
                                disabled={
                                  account.response_roster_source !==
                                    "published" &&
                                  !(
                                    allowInProgressReport &&
                                    account.response_roster_source ===
                                      "running" &&
                                    Boolean(account.report_assessment_id)
                                  )
                                }
                                className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-purple-200 bg-purple-50 px-3 py-1.5 text-sm font-medium text-purple-700 hover:bg-purple-100 disabled:cursor-not-allowed disabled:opacity-50"
                                onClick={() => onViewFullReport?.(account)}
                              >
                                Report
                                <ArrowRight className="h-3.5 w-3.5" />
                              </button>
                            </div>
                            {(account.response_roster ?? []).length > 0 ? (
                              <RosterAccordion
                                people={account.response_roster!}
                              />
                            ) : (
                              <p className="text-sm text-gray-500">
                                {account.org_status_phase === "none"
                                  ? "No assessment"
                                  : "No assessment responses available for this account yet."}
                              </p>
                            )}
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default PortfolioAccountsStatusPanel;
